import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core'

/**
 * Shared chrome for every control in the Quote Settings dialog, taken from the design prototype
 * (`source.js` primitives `LY`/`RY`/`zY`/`AY`/`NY`/`BY`/`VY`/`HY`/`IY`). One hook so the visual
 * language lives in a single place and each control file stays presentational.
 *
 * The prototype's hex values are brand Neutral tokens, so they are referenced through the theme here
 * rather than hard-coded:
 *   #f7f8fa = P98 (dialog + hover fill)   #eff2f6 = P96
 *   #e8ebf1 = P94                         #d9dee8 = P90 (toggle track)
 *   #c6cedc = P85 (borders, hairlines)    #929fb5 = P70 (placeholder)
 *   #738198 = P60 (muted / subtitle)      #55647c = P50 (caret, unit)
 *   #354962 = P40 (row label + value)     #2e3b51 = P35 (pill value)
 *
 * The accent blue and the toggle green are the fixed brand/semantic colors, NOT
 * `theme.palette.primary` — primary is the company's configurable color and can be near-white, which
 * would make a toggle or a focus ring invisible.
 */

/** Body font size the prototype uses for nearly every label and value. */
export const SETTINGS_FONT = 13

/** Radius the prototype gives cards, pills, inputs and menus. */
export const SETTINGS_RADIUS = 10

/** Height of a pill control (dropdown trigger, action button, numeric field). */
export const SETTINGS_CONTROL_HEIGHT = 32

/** Border used for cards, pills and inputs (0.8px, as in the prototype). */
export const settingsBorder = (theme: Theme) => `0.8px solid ${theme.paintscout.palette.brand.Neutral.P85}`

export const useSettingsStyles = makeStyles(
  (theme: Theme) => {
    const { brand, semantic } = theme.paintscout.palette
    const border = settingsBorder(theme)
    return {
      /**
       * Vertical rhythm for a tab's stack of sections. The gap lives here rather than on the section
       * header, so a section with no title is spaced the same as a titled one.
       */
      tab: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
        width: '100%'
      },
      /** One section: its header (when titled) sits directly above its card. */
      section: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%'
      },
      sectionHeader: {
        height: theme.spacing(4),
        display: 'flex',
        alignItems: 'center',
        // Indented to the card's inner padding, so header text lines up with the row labels inside it.
        padding: theme.spacing(0, 2),
        boxSizing: 'border-box'
      },
      sectionHeaderText: {
        fontSize: SETTINGS_FONT,
        fontWeight: 400,
        color: brand.Neutral.P60,
        lineHeight: 'normal'
      },
      card: {
        backgroundColor: brand.Neutral.White,
        border,
        borderRadius: SETTINGS_RADIUS,
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        width: '100%',
        boxSizing: 'border-box'
      },
      divider: {
        height: 0,
        width: '100%',
        borderTop: border
      },
      row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        // The search palette flashes a ring on the row it jumped to; keep it inside the card.
        borderRadius: theme.borderRadius.md,
        transition: 'box-shadow 0.2s',
        [theme.breakpoints.down('xs')]: {
          alignItems: 'flex-start',
          flexDirection: 'column',
          gap: theme.spacing(1.5)
        }
      },
      /**
       * The ring the search palette flashes on the row it jumped to. Applied imperatively by the dialog
       * (the row lives inside whichever tab just mounted), so it is a class rather than an inline style —
       * that keeps the colour on the theme instead of a hardcoded value at the call site.
       */
      rowHighlight: {
        boxShadow: `inset 0 0 0 1.5px ${brand.Blue.Blue55}`
      },
      /** Rows whose control is tall (a textarea, a note panel) read better stacked at every width. */
      rowStacked: {
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: theme.spacing(1.5)
      },
      // Takes the leftover width and is allowed to shrink, so a long subtitle (the Wisetack deposit
      // explanation, for one) wraps inside the card instead of pushing the row past its edge. The
      // control keeps its size — `rowControl` is the one that does not shrink.
      rowLabels: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minWidth: 0
      },
      rowLabel: {
        fontSize: SETTINGS_FONT,
        fontWeight: 400,
        color: brand.Neutral.P40,
        letterSpacing: '-0.42px',
        lineHeight: 1.55
      },
      rowSubtitle: {
        fontSize: SETTINGS_FONT,
        fontWeight: 400,
        color: brand.Neutral.P60,
        letterSpacing: '-0.36px',
        lineHeight: 1.55
      },
      rowControl: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexShrink: 0,
        maxWidth: '100%',
        [theme.breakpoints.down('xs')]: {
          alignSelf: 'flex-end'
        }
      },
      // --- Pill control (dropdown trigger / chip holder) ---
      pill: {
        appearance: 'none',
        backgroundColor: brand.Neutral.White,
        border,
        borderRadius: SETTINGS_RADIUS,
        minHeight: SETTINGS_CONTROL_HEIGHT,
        padding: theme.spacing(0, 1),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexShrink: 0,
        boxSizing: 'border-box',
        font: 'inherit',
        textAlign: 'left',
        maxWidth: '100%'
      },
      pillClickable: {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: brand.Neutral.P98
        }
      },
      pillDisabled: {
        cursor: 'default'
      },
      pillInner: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        minWidth: 0,
        flex: 1
      },
      pillText: {
        fontSize: SETTINGS_FONT,
        fontWeight: 400,
        color: brand.Neutral.P35,
        lineHeight: 'normal',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      },
      pillPlaceholder: {
        color: brand.Neutral.P70
      },
      pillCaret: {
        fontSize: 16,
        color: brand.Neutral.P50,
        flexShrink: 0
      },
      pillChips: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        flexWrap: 'wrap',
        minWidth: 0,
        padding: theme.spacing(0.5, 0)
      },
      // --- Inline text input ---
      input: {
        height: theme.spacing(4.5),
        backgroundColor: brand.Neutral.White,
        border,
        borderRadius: SETTINGS_RADIUS,
        padding: theme.spacing(0, 2, 0, 1.5),
        fontSize: SETTINGS_FONT,
        fontWeight: 400,
        color: brand.Neutral.P40,
        fontFamily: 'inherit',
        outline: 'none',
        boxSizing: 'border-box',
        flexShrink: 0,
        '&::placeholder': {
          color: brand.Neutral.P85
        },
        '&:focus': {
          borderColor: brand.Blue.Blue55
        }
      },
      inputFullWidth: {
        width: '100%'
      },
      textarea: {
        minHeight: theme.spacing(15),
        resize: 'vertical',
        height: 'auto',
        padding: theme.spacing(1, 2, 1, 1.5),
        lineHeight: 1.55
      },
      // --- Action button (a pill holding a command rather than a value) ---
      actionButton: {
        justifyContent: 'center',
        padding: theme.spacing(0, 1.5)
      },
      actionButtonScary: {
        borderColor: semantic.Danger.S60,
        '& > span': {
          color: semantic.Danger.S60
        }
      },
      // --- Numeric input with an optional unit picker inside the pill ---
      numberWrap: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        height: SETTINGS_CONTROL_HEIGHT,
        backgroundColor: brand.Neutral.White,
        border,
        borderRadius: SETTINGS_RADIUS,
        padding: theme.spacing(0, 0.5, 0, 1.5),
        boxSizing: 'border-box',
        flexShrink: 0,
        '&:focus-within': {
          borderColor: brand.Blue.Blue55
        }
      },
      numberWrapDisabled: {
        backgroundColor: brand.Neutral.P98
      },
      numberInput: {
        flex: 1,
        minWidth: 0,
        border: 'none',
        outline: 'none',
        background: 'transparent',
        textAlign: 'right',
        fontSize: SETTINGS_FONT,
        fontWeight: 400,
        color: brand.Neutral.P40,
        fontFamily: 'inherit'
      },
      unitButton: {
        appearance: 'none',
        border: 'none',
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.25),
        padding: theme.spacing(0, 0.5),
        font: 'inherit',
        fontSize: SETTINGS_FONT,
        color: brand.Neutral.P50,
        cursor: 'pointer',
        flexShrink: 0
      },
      unitStatic: {
        fontSize: SETTINGS_FONT,
        color: brand.Neutral.P50,
        paddingRight: theme.spacing(0.5),
        flexShrink: 0
      },
      // --- Checkbox (the prototype's `gX`) ---
      checkboxRow: {
        appearance: 'none',
        border: 'none',
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        padding: 0,
        font: 'inherit',
        cursor: 'pointer',
        flexShrink: 0
      },
      checkboxRowDisabled: {
        cursor: 'default',
        opacity: 0.5
      },
      // 16px box with a 1.14px rule, as in the prototype — geometry, so it stays in px.
      checkboxBox: {
        width: 16,
        height: 16,
        borderRadius: 4.5,
        flexShrink: 0,
        border: `1.14px solid ${brand.Neutral.P80}`,
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.12s, border-color 0.12s'
      },
      checkboxBoxChecked: {
        borderColor: brand.Blue.Blue55,
        backgroundColor: brand.Blue.Blue55
      },
      checkboxTick: {
        width: 10,
        height: 8
      },
      checkboxLabel: {
        fontSize: SETTINGS_FONT,
        fontWeight: 400,
        color: brand.Neutral.P35,
        letterSpacing: '-0.42px',
        lineHeight: 'normal',
        whiteSpace: 'nowrap'
      },
      /** Two or more checkboxes sitting side by side under a label row. */
      checkboxGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2.5),
        flexWrap: 'wrap',
        width: '100%'
      },
      // --- Toggle ---
      toggle: {
        appearance: 'none',
        padding: 0,
        border: 'none',
        width: 23,
        height: 14,
        borderRadius: theme.borderRadius.full,
        flexShrink: 0,
        cursor: 'pointer',
        backgroundColor: brand.Neutral.P90,
        transition: 'background-color 0.18s',
        position: 'relative'
      },
      toggleOn: {
        backgroundColor: semantic.Success.S70
      },
      toggleOnBlue: {
        backgroundColor: brand.Blue.Blue55
      },
      toggleDisabled: {
        cursor: 'default',
        opacity: 0.5
      },
      // The knob's offsets are geometry inside a 23x14 track, not layout spacing, so they stay in px.
      toggleKnob: {
        position: 'absolute',
        top: 2,
        left: 2,
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: brand.Neutral.White,
        transition: 'left 0.18s'
      },
      toggleKnobOn: {
        left: 11
      },
      // --- Menus (shared paper styling for every dropdown in the dialog) ---
      menuPaper: {
        border,
        borderRadius: SETTINGS_RADIUS,
        boxShadow: theme.shadows[8],
        minWidth: 175
      },
      menuItemInner: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        width: '100%',
        fontSize: SETTINGS_FONT,
        color: brand.Neutral.P40
      },
      menuCheck: {
        fontSize: 16,
        marginLeft: 'auto',
        color: brand.Blue.Blue55
      },
      /**
       * A searchable menu puts its search box at the very top, where MUI's default `MenuList` padding
       * would show as a white strip above it. Drop the top padding for those menus only — the plain
       * menus keep theirs.
       */
      menuSearchList: {
        paddingTop: 0
      },
      menuSearch: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5),
        backgroundColor: brand.Neutral.P98,
        borderBottom: border
      },
      menuSearchIcon: {
        fontSize: 16,
        color: brand.Neutral.P50,
        flexShrink: 0
      },
      menuSearchInput: {
        flex: 1,
        minWidth: 0,
        border: 'none',
        outline: 'none',
        background: 'transparent',
        fontSize: SETTINGS_FONT,
        fontWeight: 400,
        color: brand.Neutral.P40,
        lineHeight: 1.55,
        fontFamily: 'inherit',
        '&::placeholder': {
          color: brand.Neutral.P85
        }
      },
      // --- Checkbox shown at the end of a searchable menu row (Tags) ---
      menuCheckbox: {
        width: 16,
        height: 16,
        borderRadius: theme.borderRadius.md,
        flexShrink: 0,
        marginLeft: 'auto',
        boxSizing: 'border-box',
        border: `1.14px solid ${brand.Neutral.P80}`,
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.12s, border-color 0.12s'
      },
      menuCheckboxChecked: {
        borderColor: brand.Blue.Blue55,
        backgroundColor: brand.Blue.Blue55
      },
      menuCheckboxIcon: {
        fontSize: 12,
        color: brand.Neutral.White
      },
      // --- Chip (a tag inside a control) ---
      chip: {
        borderRadius: theme.borderRadius.lg,
        height: theme.spacing(3),
        padding: theme.spacing(0, 1),
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        flexShrink: 0,
        fontSize: 12,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        border,
        backgroundColor: brand.Neutral.P98,
        color: brand.Neutral.P40
      },
      dot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        flexShrink: 0
      }
    }
  },
  { name: 'QuoteSettingsControls' }
)
